/**
 * ElecBid — Jobber Auto-Fill Component
 * "Sync to Jobber" button for individual jobs + batch sync panel
 */

import { useState, useCallback } from "react";
import { apiFetch } from "../lib/apiFetch";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a303c",
  lime: "#a5ce3a", limeDim: "rgba(165,206,58,0.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,0.12)",
  red: "#ef4444", redDim: "rgba(239,68,68,0.12)",
  amber: "#f59e0b", amberDim: "rgba(245,158,11,0.12)",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,0.12)",
  text: "#f0f4f8", muted: "#8892a4", dim: "#4a5568",
};

// ─── Single Job Sync Button ──────────────────────────────────────────────────
export function SyncToJobberButton({ jobId, jobNumber, clientName, onComplete, compact = false }) {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const handleSync = async () => {
    setSyncing(true);
    setResult(null);
    try {
      const res = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "autoFillJob", jobId }),
      });
      const data = await res.json();
      setResult(data);
      if (onComplete) onComplete(data);
    } catch (err) {
      setResult({ ok: false, error: err.message });
    } finally {
      setSyncing(false);
    }
  };

  if (compact) {
    return (
      <div>
        <button onClick={handleSync} disabled={syncing} style={{
          padding: "6px 14px", borderRadius: 8, border: "none",
          cursor: syncing ? "default" : "pointer",
          background: syncing ? C.dim : C.lime, color: "#000",
          fontWeight: 700, fontSize: 12, opacity: syncing ? 0.6 : 1,
          transition: "all 0.15s", whiteSpace: "nowrap",
        }}>
          {syncing ? "Syncing..." : "Sync to Jobber"}
        </button>
        {result && (
          <div style={{ marginTop: 4, fontSize: 11, color: result.ok ? C.green : C.red }}>
            {result.ok ? "Synced" : `Failed: ${result.error || "Unknown"}`}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Auto-Fill Jobber Fields</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            Calculates metrics from timesheets, visits, expenses &amp; quote data — pushes to Jobber custom fields.
          </div>
        </div>
        <button onClick={handleSync} disabled={syncing} style={{
          padding: "10px 24px", borderRadius: 10, border: "none",
          cursor: syncing ? "default" : "pointer",
          background: syncing ? C.dim : `linear-gradient(135deg, ${C.lime}, #8ab82e)`,
          color: "#000", fontWeight: 800, fontSize: 14, opacity: syncing ? 0.6 : 1,
          transition: "all 0.15s", boxShadow: syncing ? "none" : `0 2px 12px ${C.limeDim}`,
        }}>
          {syncing ? "Computing & Syncing..." : "Sync to Jobber"}
        </button>
      </div>

      {result && (
        <div style={{
          marginTop: 12, padding: 12, borderRadius: 8,
          background: result.ok ? C.greenDim : C.redDim,
          border: `1px solid ${result.ok ? C.green : C.red}`,
        }}>
          {result.ok ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.green, marginBottom: 8 }}>
                Auto-fill complete for Job #{result.metrics?.job_number || jobNumber}
              </div>
              <MetricsPreview metrics={result.metrics} sources={result.sources} />
              {result.push?.skipped && (
                <div style={{ fontSize: 11, color: C.amber, marginTop: 6 }}>
                  Custom fields not pushed — no matching field mappings found in Jobber
                </div>
              )}
              {result.push?.errors?.length > 0 && (
                <div style={{ fontSize: 11, color: C.red, marginTop: 6 }}>
                  Push errors: {result.push.errors.map(e => e.message).join(", ")}
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 13, color: C.red }}>
              Auto-fill failed: {result.error || result.details || "Unknown error"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MetricsPreview({ metrics, sources }) {
  if (!metrics) return null;
  const rows = [
    { label: "Job Type", value: metrics.job_type },
    { label: "Complexity", value: metrics.job_complexity },
    { label: "Lead Tech", value: metrics.lead_tech },
    { label: "Jman Hours", value: metrics.jman_hours_actual != null ? `${metrics.jman_hours_actual}h` : null },
    { label: "Apprentice Hours", value: metrics.apprentice_hours_actual != null ? `${metrics.apprentice_hours_actual}h` : null },
    { label: "Drive/Supplier", value: metrics.drive_supplier_time != null ? `${metrics.drive_supplier_time}h` : null },
    { label: "Expenses", value: metrics.expenses_actual != null ? `$${metrics.expenses_actual.toLocaleString()}` : null },
    { label: "Purchases", value: metrics.supplier_purchase_count },
    { label: "Completed", value: metrics.work_completed_date },
    { label: "Callbacks", value: metrics.callback_visits },
    { label: "Close-out", value: metrics.close_out_score },
    { label: "Variance", value: metrics.variance_pct != null ? `${metrics.variance_pct > 0 ? "+" : ""}${metrics.variance_pct}%` : null },
    { label: "Reason", value: metrics.variance_reason },
  ].filter(r => r.value != null && r.value !== "");

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ background: "rgba(0,0,0,0.2)", borderRadius: 6, padding: "3px 8px", fontSize: 11 }}>
          <span style={{ color: C.muted }}>{r.label}: </span>
          <span style={{ color: C.text, fontWeight: 600 }}>{r.value}</span>
        </div>
      ))}
      {sources && (
        <div style={{ background: "rgba(0,0,0,0.2)", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: C.muted }}>
          {sources.timesheetCount} timesheets · {sources.visitCount} visits · {sources.expenseCount} expenses
          {sources.hasQuote ? " · quote" : ""}{sources.hasAssessment ? " · assessment" : ""}
        </div>
      )}
    </div>
  );
}

// ─── Batch Sync Panel ────────────────────────────────────────────────────────
export function BatchSyncPanel() {
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedJobs, setSelectedJobs] = useState(new Set());

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setResults(null);
    try {
      const res = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "fetchRecentJobs", status: "completed", limit: 50 }),
      });
      const data = await res.json();
      if (data.ok) {
        setJobs(data.jobs || []);
        setSelectedJobs(new Set(data.jobs.map(j => j.id)));
      } else {
        setJobs([]);
      }
    } catch { setJobs([]); }
    finally { setLoading(false); }
  }, []);

  const runBatch = async () => {
    if (selectedJobs.size === 0) return;
    setSyncing(true);
    setResults(null);
    try {
      const res = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "autoFillBatch", jobIds: Array.from(selectedJobs) }),
      });
      setResults(await res.json());
    } catch (err) {
      setResults({ ok: false, error: err.message });
    } finally {
      setSyncing(false);
    }
  };

  const toggleJob = (id) => {
    setSelectedJobs(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleAll = () => {
    setSelectedJobs(selectedJobs.size === jobs?.length ? new Set() : new Set(jobs.map(j => j.id)));
  };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Batch Auto-Fill</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            Pull recent completed jobs from Jobber, compute metrics, push custom fields in bulk.
          </div>
        </div>
        {!jobs && (
          <button onClick={loadJobs} disabled={loading} style={{
            padding: "10px 20px", borderRadius: 10, border: "none",
            cursor: loading ? "default" : "pointer",
            background: loading ? C.dim : C.lime, color: "#000",
            fontWeight: 800, fontSize: 13, opacity: loading ? 0.6 : 1,
          }}>
            {loading ? "Loading..." : "Load Jobs from Jobber"}
          </button>
        )}
      </div>

      {jobs && jobs.length > 0 && (
        <>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, marginBottom: 10,
            paddingBottom: 10, borderBottom: `1px solid ${C.border}`,
          }}>
            <button onClick={toggleAll} style={{
              padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
              background: C.surface, color: C.text, cursor: "pointer", fontSize: 12,
            }}>
              {selectedJobs.size === jobs.length ? "Deselect All" : "Select All"}
            </button>
            <span style={{ fontSize: 12, color: C.muted }}>{selectedJobs.size} / {jobs.length} selected</span>
            <button onClick={runBatch} disabled={syncing || selectedJobs.size === 0} style={{
              marginLeft: "auto", padding: "8px 18px", borderRadius: 8, border: "none",
              cursor: syncing || selectedJobs.size === 0 ? "default" : "pointer",
              background: syncing ? C.dim : `linear-gradient(135deg, ${C.lime}, #8ab82e)`,
              color: "#000", fontWeight: 800, fontSize: 13,
              opacity: syncing || selectedJobs.size === 0 ? 0.5 : 1,
            }}>
              {syncing ? "Processing..." : `Sync ${selectedJobs.size} Jobs`}
            </button>
          </div>

          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {jobs.map(j => (
              <label key={j.id} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                borderRadius: 8, cursor: "pointer",
                background: selectedJobs.has(j.id) ? C.limeDim : "transparent",
                marginBottom: 2, transition: "background 0.1s",
              }}>
                <input type="checkbox" checked={selectedJobs.has(j.id)} onChange={() => toggleJob(j.id)}
                  style={{ accentColor: C.lime }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                    #{j.jobNumber} — {j.client?.name || "Unknown"}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {j.title || "Untitled"} · {j.total ? `$${parseFloat(j.total).toLocaleString()}` : "—"}
                  </div>
                </div>
                {j.closedAt && (
                  <div style={{ fontSize: 11, color: C.dim }}>
                    {new Date(j.closedAt).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                  </div>
                )}
              </label>
            ))}
          </div>
        </>
      )}

      {jobs && jobs.length === 0 && (
        <div style={{ textAlign: "center", padding: 20, color: C.muted, fontSize: 13 }}>
          No completed jobs found in Jobber
        </div>
      )}

      {results && (
        <div style={{
          marginTop: 16, padding: 14, borderRadius: 10,
          background: results.ok && results.failed === 0 ? C.greenDim :
                     results.ok && results.failed > 0 ? C.amberDim : C.redDim,
          border: `1px solid ${results.ok && results.failed === 0 ? C.green :
                  results.ok && results.failed > 0 ? C.amber : C.red}`,
        }}>
          {results.ok ? (
            <>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.green, marginBottom: 8 }}>
                Batch complete: {results.success}/{results.total} synced
                {results.failed > 0 && <span style={{ color: C.red }}> · {results.failed} failed</span>}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {results.results?.map((r, i) => (
                  <div key={i} style={{
                    padding: "2px 8px", borderRadius: 6, fontSize: 11,
                    background: "rgba(0,0,0,0.2)", color: r.ok ? C.green : C.red,
                  }}>
                    #{r.jobNumber || "?"} {r.ok ? "OK" : "FAIL"}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: C.red }}>Batch failed: {results.error || "Unknown"}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default SyncToJobberButton;
